import { useState, useEffect } from 'react';
import { Search, Car, X, Clock, AlertTriangle } from 'lucide-react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { api, frDate } from './api';

const vLabel = (v) => `${v.nom || v.modele || ''} · commercial d'origine : ${v.commercial || '—'} · ${v.date_vente ? `vendu le ${frDate(v.date_vente)}` : 'en stock'}`;

// Recherche d'immatriculation partagée (onglets Frais véhicule et SAV) : sélectionner un véhicule ne crée jamais de dépense ni de retenue
export function VehicleSearch({ rid, initial, onPick, onKeepPlate, busy }) {
    const [q, setQ] = useState(initial || '');
    const [res, setRes] = useState([]);
    const [done, setDone] = useState(false);
    useEffect(() => {
        setDone(false);
        if (q.trim().length < 2) { setRes([]); return undefined; }
        const t = setTimeout(() => api.searchVehicles(q, rid).then(setRes).catch(() => setRes([])).finally(() => setDone(true)), 250);
        return () => clearTimeout(t);
    }, [q, rid]);
    const plate = q.trim().toUpperCase();
    return (
        <div className="mt-2 relative" data-testid="invoice-vehicle-search-box">
            <Search className="w-4 h-4 text-gray-400 absolute left-2.5 top-2.5" />
            <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Saisir une immatriculation (ex. AB-123-CD) ou un modèle" className="h-9 pl-8 text-[13px] font-mono" data-testid="invoice-vehicle-search" />
            {q && <button onClick={() => setQ('')} className="absolute right-2 top-2.5 text-gray-400" aria-label="Effacer" data-testid="invoice-vehicle-search-clear"><X className="w-4 h-4" /></button>}
            <div className="mt-1 max-h-56 overflow-auto rounded-lg border border-gray-100 divide-y divide-gray-50">
                {res.map(v => (
                    <button key={v.id} onClick={() => onPick(v)} className="w-full text-left px-3 py-2 hover:bg-gray-50 text-[12px]" data-testid={`invoice-vehicle-option-${v.id}`}>
                        <span className="font-mono font-semibold">{v.immatriculation}</span> <span className="text-gray-500">{vLabel(v)}</span>
                    </button>
                ))}
                {done && q.trim().length >= 2 && !res.length && (
                    <div className="px-3 py-2 space-y-2" data-testid="invoice-vehicle-not-found">
                        <p className="text-[12px] text-gray-500">Aucun véhicule de cette agence ne correspond à « {plate} ».</p>
                        {onKeepPlate && plate.length >= 4 && (
                            <Button size="sm" variant="outline" disabled={busy} onClick={() => onKeepPlate(plate)} className="h-8 text-[12px]" data-testid="invoice-vehicle-keep-plate">
                                <Clock className="w-3.5 h-3.5 mr-1" />Garder la plaque {plate} et laisser en attente
                            </Button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}

export function VehiclePicker({ vehicle, onPick, onClear, disabled, rid, pendingPlate, onKeepPlate, busy }) {
    const [open, setOpen] = useState(!vehicle);
    useEffect(() => { setOpen(!vehicle); }, [vehicle]);
    return (
        <div data-testid="invoice-vehicle-picker">
            <div className="flex items-center justify-between gap-2">
                {vehicle ? (
                    <div className="flex items-start gap-2 min-w-0" data-testid="invoice-vehicle-current">
                        <Car className="w-4 h-4 text-blue-600 shrink-0 mt-0.5" />
                        <div className="min-w-0">
                            <span className="font-mono text-[13px] font-semibold text-gray-900 whitespace-nowrap">{vehicle.immatriculation}</span>
                            <p className="text-[12px] text-gray-500 break-words" data-testid="invoice-vehicle-commercial">{vLabel(vehicle)}</p>
                        </div>
                    </div>
                ) : <span className="text-[13px] text-red-600" data-testid="invoice-vehicle-none">Aucun véhicule sélectionné</span>}
                {!disabled && vehicle && (
                    <div className="flex flex-col items-end gap-1 shrink-0">
                        <button onClick={() => setOpen(o => !o)} className="text-[12px] text-[#6366F1] font-medium" data-testid="invoice-vehicle-change">{open ? 'Fermer' : 'Changer de véhicule'}</button>
                        {onClear && <button onClick={onClear} disabled={busy} className="text-[12px] text-red-600 font-medium" data-testid="invoice-vehicle-clear">Retirer l'attribution</button>}
                    </div>
                )}
            </div>
            {!vehicle && pendingPlate && (
                <p className="mt-1.5 flex items-center gap-1.5 text-[12px] text-amber-700" data-testid="invoice-vehicle-pending-plate">
                    <AlertTriangle className="w-3.5 h-3.5" />Plaque <b className="font-mono">{pendingPlate}</b> : véhicule introuvable, facture en attente de rattachement
                </p>
            )}
            {open && !disabled && <VehicleSearch key={pendingPlate || ''} rid={rid} initial={vehicle ? '' : pendingPlate} busy={busy} onKeepPlate={onKeepPlate} onPick={v => { onPick(v); setOpen(false); }} />}
        </div>
    );
}
